import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global route-change scroll handler.
 *
 * React Router does not reset scroll when you navigate via <Link>, so a user
 * clicking a footer link on a long page lands at the bottom of the new
 * page instead of the top. This component fixes that for every route change.
 *
 * Behavior:
 *  - New pathname: scroll to top instantly.
 *  - Same pathname with a new hash: let the browser scroll to the anchor
 *    (we find the element and scroll to it ourselves because React Router's
 *    `<Link>` doesn't honor hashes reliably on same-path navigation).
 *  - POP (back/forward) navigations: leave scroll alone so the browser's
 *    built-in scroll restoration keeps the previous position.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer one frame so the target element has mounted.
      requestAnimationFrame(() => {
        const id = hash.replace(/^#/, '');
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
      return;
    }

    // No hash — reset to the top of the document.
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
